import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import {
  PlusIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  ClockIcon,
  UserPlusIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import {
  OnboardingRequestsClient,
  OnboardingRequestDto,
  RequestStatus
} from '../../web-api-client';
import { toast } from 'sonner';
import { API_CONFIG } from '../../config/api';
import { useUserEmail } from '../../hooks/useUserEmail';
import { createAuthenticatedFetch } from '../../utils/apiClient';
import NewOnboardingRequestForm from './NewOnboardingRequestForm';
import { DataTable } from '../../components/ui/data-table';
import { Button } from '../../components/ui/button';
import { RequestStatusBadge } from '../../components/ui/status-badge';
import { ERROR_MESSAGES, LOADING_TEXT } from '../../constants/messages';

// Component to display submitted date and submitter
function SubmittedInfo({ submittedDate, submittedBy }: { submittedDate: Date; submittedBy?: string }) {
  const email = useUserEmail(submittedBy);

  const formattedDate = new Date(submittedDate.toString() + 'Z').toLocaleDateString();
  const submitter = email || 'Unknown';

  return (
    <div className="flex items-center">
      <ClockIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
      <span>Submitted: {formattedDate} by {submitter}</span>
    </div>
  );
}

type ViewMode = 'active' | 'completed';

export default function Onboarding() {
  const navigate = useNavigate();
  const [showNewRequestModal, setShowNewRequestModal] = useState(false);
  const [requests, setRequests] = useState<OnboardingRequestDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('active');
  const [showCompleted, setShowCompleted] = useState(true);
  const [showRejected, setShowRejected] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Filter options for status - changes based on view mode
  const statusOptions = useMemo(() => {
    if (viewMode === 'active') {
      return [
        { value: 'all', label: 'All Active' },
        { value: 'draft', label: 'Draft' },
        { value: 'pending', label: 'Pending Approval' },
        { value: 'approved', label: 'Approved' },
        { value: 'inprogress', label: 'In Progress' },
      ];
    } else {
      return [
        { value: 'all', label: 'All Completed' },
      ];
    }
  }, [viewMode]);

  // Reset status filter when view mode changes
  useEffect(() => {
    setStatusFilter('all');
  }, [viewMode]);

  // Filtered requests based on view mode and status
  const filteredRequests = useMemo(() => {
    // First filter by view mode
    let filtered = requests.filter(r => {
      const status = Number(r.status);
      if (viewMode === 'active') {
        // Active: Draft, Pending, Approved, In Progress
        return status === RequestStatus.Draft ||
               status === RequestStatus.Pending ||
               status === RequestStatus.PendingFirstApproval ||
               status === RequestStatus.PendingSecondApproval ||
               status === RequestStatus.Approved ||
               status === RequestStatus.InProgress;
      } else {
        // Completed view: optionally Completed + optionally Rejected/Cancelled
        const isCompleted = status === RequestStatus.Completed;
        const isRejected = status === RequestStatus.Rejected || status === RequestStatus.Cancelled;

        return (showCompleted && isCompleted) || (showRejected && isRejected);
      }
    });

    // Then apply status filter (only for active view)
    if (viewMode === 'active' && statusFilter !== 'all') {
      filtered = filtered.filter(r => {
        const status = Number(r.status);
        switch (statusFilter) {
          case 'draft':
            return status === RequestStatus.Draft;
          case 'pending':
            return status === RequestStatus.Pending ||
                   status === RequestStatus.PendingFirstApproval ||
                   status === RequestStatus.PendingSecondApproval;
          case 'approved':
            return status === RequestStatus.Approved;
          case 'inprogress':
            return status === RequestStatus.InProgress;
          default:
            return true;
        }
      });
    }

    return filtered;
  }, [requests, viewMode, showCompleted, showRejected, statusFilter]);

  // Counts for the toggle badges
  const activeCount = useMemo(() => {
    return requests.filter(r => {
      const status = Number(r.status);
      return status === RequestStatus.Draft ||
             status === RequestStatus.Pending ||
             status === RequestStatus.PendingFirstApproval ||
             status === RequestStatus.PendingSecondApproval ||
             status === RequestStatus.Approved ||
             status === RequestStatus.InProgress;
    }).length;
  }, [requests]);

  const completedCount = useMemo(() => {
    return requests.filter(r => Number(r.status) === RequestStatus.Completed).length;
  }, [requests]);

  // Define table columns
  const columns: ColumnDef<OnboardingRequestDto>[] = [
    {
      accessorKey: 'employeeName',
      header: 'Employee',
      cell: ({ row }) => (
        <div>
          <div className="font-medium text-slate-900 dark:text-white">{row.original.employeeName}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">{row.original.locationName}</div>
        </div>
      ),
    },
    {
      accessorKey: 'designation',
      header: 'Role & Dept',
      cell: ({ row }) => (
        <div>
          <div className="text-slate-900 dark:text-white">{row.original.designation}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">{row.original.departmentName}</div>
        </div>
      ),
    },
    {
      accessorKey: 'employmentDate',
      header: 'Start Date',
      cell: ({ row }) => (
        <div className="text-slate-600 dark:text-slate-300">
          {row.original.employmentDate
            ? new Date(row.original.employmentDate + 'Z').toLocaleDateString()
            : '-'}
        </div>
      ),
    },
    {
      id: 'progress',
      header: 'Progress',
      cell: ({ row }) => {
        const totalTasks = row.original.totalTasks || 0;
        const completedTasks = row.original.completedTasks || 0;
        const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

        if (row.original.status !== RequestStatus.Approved &&
            row.original.status !== RequestStatus.InProgress &&
            row.original.status !== RequestStatus.Completed) {
          return <div className="text-xs text-slate-400">N/A</div>;
        }

        return (
          <div className="w-48">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-medium dark:text-gray-300">{progress}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <RequestStatusBadge status={row.original.status} />
      ),
    },
    {
      id: 'actions',
      header: () => <div className="text-right">Action</div>,
      cell: ({ row }) => (
        <div className="flex justify-end gap-2">
          {row.original.status === RequestStatus.Draft && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/onboarding/${row.original.id}/edit`)}
              className="text-blue-600 hover:text-blue-900"
              title="Edit"
            >
              <PencilIcon className="w-5 h-5" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/onboarding/${row.original.id}`)}
            className="text-slate-600 hover:text-slate-900"
            title="View Details"
          >
            <ChevronRightIcon className="w-5 h-5" />
          </Button>
        </div>
      ),
    },
  ];

  // Fetch onboarding requests list
  useEffect(() => {
    const fetchRequests = async () => {
      try {
        setLoading(true);
        const client = new OnboardingRequestsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());
        const data = await client.getOnboardingRequests();
        setRequests(data);
      } catch (error) {
        console.error(ERROR_MESSAGES.FETCH_FAILED('onboarding requests'), error);
        toast.error(ERROR_MESSAGES.FETCH_FAILED('onboarding requests'));
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, [showNewRequestModal]); // Refetch when modal closes

  const handleFormSuccess = () => {
    // This will trigger the useEffect to refetch requests
    setShowNewRequestModal(false);
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Onboarding Requests</h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Manage and track employee onboarding processes
            </p>
          </div>
          <button
            onClick={() => setShowNewRequestModal(true)}
            className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl shadow-lg hover:shadow-xl text-white font-medium transition-all duration-200 hover:scale-105"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            New Onboarding Request
          </button>
        </div>
      </div>

      {/* New Onboarding Request Form */}
      <NewOnboardingRequestForm 
        show={showNewRequestModal}
        onClose={() => setShowNewRequestModal(false)}
        onSuccess={handleFormSuccess}
      />

      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow-sm rounded-xl">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserPlusIcon className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Total Requests</dt>
                  <dd className="text-lg font-semibold text-gray-900 dark:text-white">{requests.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow-sm rounded-xl">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-6 w-6 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Pending</dt>
                  <dd className="text-lg font-semibold text-gray-900 dark:text-white">
                    {requests.filter(r =>
                      r.status === RequestStatus.Pending ||
                      r.status === RequestStatus.PendingFirstApproval ||
                      r.status === RequestStatus.PendingSecondApproval
                    ).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow-sm rounded-xl">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-6 w-6 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Approved</dt>
                  <dd className="text-lg font-semibold text-gray-900 dark:text-white">
                    {requests.filter(r => r.status === RequestStatus.Approved || r.status === RequestStatus.InProgress).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow-sm rounded-xl">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Completed</dt>
                  <dd className="text-lg font-semibold text-gray-900 dark:text-white">
                    {requests.filter(r => r.status === RequestStatus.Completed).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Onboarding Requests List */}
      <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl overflow-hidden p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500 dark:text-gray-400">{LOADING_TEXT.DEFAULT}</div>
          </div>
        ) : requests.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <div className="text-gray-400 dark:text-gray-500 mb-2">
              <UserPlusIcon className="h-12 w-12" />
            </div>
            <p className="text-gray-600 dark:text-gray-300 font-medium">No onboarding requests yet</p>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Create your first request to get started</p>
            <div className="mt-6">
              <button
                onClick={() => setShowNewRequestModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                New Onboarding Request
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* View Mode Toggle & Filters */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
              {/* Active/Completed Toggle */}
              <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('active')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'active'
                      ? 'bg-white dark:bg-gray-600 text-green-700 dark:text-green-400 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  <ClockIcon className="h-4 w-4" />
                  Active
                  <span className={`ml-1 px-2 py-0.5 text-xs rounded-full ${
                    viewMode === 'active'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400'
                      : 'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300'
                  }`}>
                    {activeCount}
                  </span>
                </button>
                <button
                  onClick={() => setViewMode('completed')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'completed'
                      ? 'bg-white dark:bg-gray-600 text-green-700 dark:text-green-400 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  <CheckCircleIcon className="h-4 w-4" />
                  Completed
                  <span className={`ml-1 px-2 py-0.5 text-xs rounded-full ${
                    viewMode === 'completed'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400'
                      : 'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300'
                  }`}>
                    {completedCount}
                  </span>
                </button>
              </div>

              {/* Status Filter (Active view) or Show Rejected checkbox (Completed view) */}
              <div className="flex items-center gap-4">
                {viewMode === 'active' ? (
                  <>
                    <div className="flex items-center gap-2">
                      <FunnelIcon className="h-5 w-5 text-gray-400" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter:</span>
                    </div>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="block w-48 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    >
                      {statusOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {statusFilter !== 'all' && (
                      <button
                        onClick={() => setStatusFilter('all')}
                        className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline"
                      >
                        Clear
                      </button>
                    )}
                  </>
                ) : (
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showCompleted}
                        onChange={(e) => setShowCompleted(e.target.checked)}
                        className="h-4 w-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Show Completed</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showRejected}
                        onChange={(e) => setShowRejected(e.target.checked)}
                        className="h-4 w-4 text-red-600 rounded border-gray-300 focus:ring-red-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Show Rejected / Cancelled</span>
                    </label>
                  </div>
                )}
              </div>
            </div>

            <DataTable
              columns={columns}
              data={filteredRequests}
              searchable={true}
              searchColumn="employeeName"
              searchPlaceholder="Search by employee name..."
            />
          </>
        )}
      </div>
    </div>
  );
}